/**
 * @file index.tsx
 * @module app/modules/price-catalog/index
 * @description Price catalog module entry point.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Button,
  Card,
  Form,
  Badge,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import APICallService from "../../../api/apiCallService";
import { manageProduct, master, vendor } from "../../../api/apiEndPoints";
import { getKey, setKey } from "../../../Global/history";
import Loader from "../../../Global/loader";
import { error, success } from "../../../Global/toast";
import {
  AllProduct,
  ApprovedProduct,
  Categories,
  PermissionModule,
  Permissions,
} from "../../../utils/constants";
import { CustomSearchInput } from "../../custom/search";
import { CustomSelect } from "../../custom/Select/CustomSelect";
import { useWarehouse, WarehouseItem } from "../../context/WarehouseContext";
import ConfirmChangesModal from "./components/ConfirmChangesModal.component";

// --- React Hook Form & Zod ---
import { useForm, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDebounce } from "../../../utils/useDebounce";
import { InfiniteScroll, RoundedSwitch } from "../../components";
import { BatchEditFormValues, batchEditSchema } from "../../../types/schemas";
import {
  calculateGPMargin,
  DbQuantityKeys,
  fetchConfigurationSettingByKey,
  getActivePrice,
  getQuantityTypeLabel,
  isValidQuantityType,
} from "../../../utils";
import { CONFIGURATION_SETTING_KEYS } from "../../../utils/configurationSettingKeys";
import {
  PriceCatalog,
  PriceCatalogListingProduct,
  OverallGrossProfit,
  ProductWithVariant,
  QuantityType,
  VariantWithPriceCatalog,
} from "../../../types";
import { Response, ResponseWithRecord } from "../../../types/response_data";
import { CriticalAlertsTab } from "./components/CriticalAlert.component";
import { useAuth, UserModel } from "../auth";
import Method from "../../../utils/methods";
import { useNavigate } from "react-router-dom";
import { ChangeHistoryTab } from "./components/ChangeHistory.component";

const listAssignProductsToVendors = {
  page: "listAssignProductsToVendorsPage",
  limit: "listAssignProductsToVendorsLimit",
  search: "listAssignProductsToVendorsSearch",
  filterPrimary: "priceCatalogFilterPrimary",
  filterSub: "priceCatalogFilterSub",
  filterSupplier: "priceCatalogFilterSupplier",
  filterBrand: "priceCatalogFilterBrand",
};

type CategoryType = {
  _id: string;
  title: string;
  image: string;
  categories?: CategoryType[];
};

// `NaN !== NaN` is true, so a plain Number() compare flags untouched rows as edited
const hasNumericChange = (oldVal: any, newVal: any) => {
  const a = Number(oldVal);
  const b = Number(newVal);
  if (!Number.isFinite(a) && !Number.isFinite(b)) return false;
  return a !== b;
};

const hasQuantityChange = (quantities: any) =>
  Object.values(quantities || {}).some(
    (q: any) =>
      !!q &&
      isValidQuantityType(q.type) &&
      (hasNumericChange(q.oldCp, q.newCp) ||
        hasNumericChange(q.oldSp, q.newSp)),
  );

const formatGP = (value?: number) =>
  Number.isFinite(Number(value)) ? `${Number(value).toFixed(2)}%` : "-";

// Bootstrap text color for a GP% value against the warehouse's Healthy/Minimum GP thresholds.
// Falls back to the pre-threshold "negative margin" red when thresholds haven't loaded yet.
const getGPColorClass = (
  costPrice: number,
  sellingPrice: number,
  healthyGP?: number,
  minimumGP?: number,
): string => {
  if (!costPrice) return "text-dark";

  if (!Number.isFinite(Number(healthyGP)) || !Number.isFinite(Number(minimumGP))) {
    return sellingPrice > 0 && costPrice >= sellingPrice
      ? "text-danger"
      : "text-dark";
  }

  const marginPercent = ((sellingPrice - costPrice) / costPrice) * 100;
  if (marginPercent >= Number(healthyGP)) return "text-success";
  if (marginPercent >= Number(minimumGP)) return "text-warning";
  return "text-danger";
};

// Solid fill color (readable with white text) for the GP% tooltip, based on current GP vs thresholds
const getGPBgColor = (
  gpValue?: number,
  healthyGP?: number,
  minimumGP?: number,
): string => {
  if (
    !Number.isFinite(Number(gpValue)) ||
    !Number.isFinite(Number(healthyGP)) ||
    !Number.isFinite(Number(minimumGP))
  ) {
    return "#2b2b40"; // default tooltip background when thresholds haven't loaded yet
  }
  if (Number(gpValue) >= Number(healthyGP)) return "#50cd89";
  if (Number(gpValue) >= Number(minimumGP)) return "#ffc700";
  return "#f1416c";
};

// Form Metadata Initializers
const initProductMetadata = (getValues: any, setValue: any, product: any) => {
  if (!getValues(`products.${product._id}.productId`)) {
    const firstVariant = product.variants?.[0];
    const priceCatalog = firstVariant?.priceCatalog || {};

    const initialTaxable = priceCatalog.taxed ?? !product.taxFree;
    const initialTaxPct =
      priceCatalog.taxPercentage ?? product.productTax ?? 18;

    setValue(`products.${product._id}.productId`, product._id);
    setValue(`products.${product._id}.productTitle`, product.title || "");
    setValue(`products.${product._id}.isTaxable`, initialTaxable);
    setValue(`products.${product._id}.oldIsTaxable`, initialTaxable);
    setValue(`products.${product._id}.taxPercentage`, initialTaxPct);
    setValue(`products.${product._id}.oldTaxPercentage`, initialTaxPct);
    setValue(`products.${product._id}.originalProductItem`, product);
  }
};

const initVariantMetadata = (
  getValues: any,
  setValue: any,
  product: any,
  variantItem: any,
) => {
  const actualVariant = variantItem.variant || variantItem;
  const variantId = actualVariant?._id || "fallback";

  if (!getValues(`variants.${variantId}.productId`)) {
    setValue(`variants.${variantId}.productId`, product._id);
    setValue(`variants.${variantId}.variantId`, variantId);
    setValue(`variants.${variantId}.productTitle`, product.title || "");
    setValue(`variants.${variantId}.variantTitle`, actualVariant?.title || "");
    setValue(
      `variants.${variantId}.draftPO`,
      variantItem?.priceCatalog?.draftPOEnabled ?? false,
    );
    setValue(
      `variants.${variantId}.oldDraftPO`,
      variantItem?.priceCatalog?.draftPOEnabled ?? false,
    );
    const initialDoih = variantItem?.priceCatalog?.daysOfInventoryHolding ?? 0;
    setValue(`variants.${variantId}.doih`, initialDoih);
    setValue(`variants.${variantId}.oldDoih`, initialDoih);
    setValue(`variants.${variantId}.originalVariantItem`, variantItem);
    setValue(`variants.${variantId}.quantities`, {});
  }
};

const initQuantityMetadata = (
  getValues: any,
  setValue: any,
  product: any,
  variantItem: any,
  qt: any,
  currentPrices: any,
  unitLabel: string,
) => {
  const actualVariant = variantItem.variant || variantItem;
  const variantId = actualVariant?._id || "fallback";

  // Ensure base variant metadata is present
  initVariantMetadata(getValues, setValue, product, variantItem);

  if (!getValues(`variants.${variantId}.quantities.${qt.type}.type`)) {
    setValue(`variants.${variantId}.quantities.${qt.type}.type`, qt.type);
    setValue(
      `variants.${variantId}.quantities.${qt.type}.unitLabel`,
      unitLabel,
    );
    setValue(
      `variants.${variantId}.quantities.${qt.type}.oldCp`,
      currentPrices.cp,
    );
    setValue(
      `variants.${variantId}.quantities.${qt.type}.oldSp`,
      currentPrices.sp,
    );
    setValue(
      `variants.${variantId}.quantities.${qt.type}.newCp`,
      currentPrices.cp,
    );
    setValue(
      `variants.${variantId}.quantities.${qt.type}.newSp`,
      currentPrices.sp,
    );
  }
};

// Isolated Save Button (Changed to type="button" to avoid silent validation blocks)
const SaveAllButton = ({
  control,
  onSave,
}: {
  control: any;
  onSave: () => void;
}) => {
  const variants = useWatch({ control, name: "variants" }) || {};
  const products = useWatch({ control, name: "products" }) || {};

  const modifiedVariantIds = new Set<string>();

  // Check Variants
  Object.values(variants).forEach((v: any) => {
    if (!v?.productId) return;
    const draftChanged = v.draftPO !== v.oldDraftPO;
    const doihChanged = hasNumericChange(v.oldDoih, v.doih);
    const qtyChanged = hasQuantityChange(v.quantities);
    if (draftChanged || doihChanged || qtyChanged)
      modifiedVariantIds.add(v.variantId);
  });

  // Check Products (Tax modifications affect all underlying variants)
  Object.values(products).forEach((p: any) => {
    if (!p?.productId) return;
    if (
      p.isTaxable !== p.oldIsTaxable ||
      Number(p.taxPercentage) !== Number(p.oldTaxPercentage)
    ) {
      p.originalProductItem?.variants?.forEach((variantItem: any) => {
        const actualVariant = variantItem.variant || variantItem;
        if (actualVariant?._id) {
          modifiedVariantIds.add(actualVariant._id);
        }
      });
    }
  });

  const count = modifiedVariantIds.size;

  return (
    <Button className="uw-btn uw-btn-primary"
      variant="primary"
      type="button"
      disabled={count === 0}
      onClick={onSave}
    >
      Save All {count > 0 && `(${count})`}
    </Button>
  );
};

// Tax Controls for Product Row
const ProductTaxControls = ({
  product,
  control,
  setValue,
  getValues,
  onMarkModified,
  currentUser,
}: any) => {
  const fieldTaxable = `products.${product._id}.isTaxable`;
  const fieldTaxPct = `products.${product._id}.taxPercentage`;

  const firstVariant = product.variants?.[0];
  const priceCatalog = firstVariant?.priceCatalog || {};

  // Custom requested initialization logic
  const initialTaxable = priceCatalog.taxed ?? !product.taxFree;
  const initialTaxPct = priceCatalog.taxPercentage ?? product.productTax ?? 18;

  const isTaxable = useWatch({
    control,
    name: fieldTaxable,
    defaultValue: initialTaxable,
  });

  return (
    <>
      <td>
        <Controller
          control={control}
          name={fieldTaxable}
          defaultValue={initialTaxable}
          render={({ field }) => (
            <Form.Check
              type="switch"
              checked={field.value ?? initialTaxable} // Fallback injected here to fix non-reflection
              onChange={(e: { target: { checked: any; }; }) => {
                initProductMetadata(getValues, setValue, product);
                onMarkModified(product);
                field.onChange(e.target.checked);
              }}
              disabled={
                !Method.hasPermission(
                  PermissionModule.PriceCatalog,
                  Permissions.PC_EditTax,
                  currentUser,
                )
              }
            />
          )}
        />
      </td>
      <td>
        <Controller
          control={control}
          name={fieldTaxPct}
          defaultValue={initialTaxPct}
          render={({ field, fieldState }) => (
            <Form.Control
              type="number"
              size="sm"
              className="w-75px"
              isInvalid={!!fieldState.error}
              value={field.value !== undefined ? field.value : initialTaxPct} // Fallback injected here
              disabled={
                !(
                  isTaxable &&
                  Method.hasPermission(
                    PermissionModule.PriceCatalog,
                    Permissions.PC_EditTax,
                    currentUser,
                  )
                )
              }
              onChange={(e: { target: { value: any; }; }) => {
                initProductMetadata(getValues, setValue, product);
                onMarkModified(product);
                field.onChange(e.target.value);
              }}
            />
          )}
        />
      </td>
    </>
  );
};

// Draft PO Control for Variant Row
const VariantDraftPOControl = ({
  product,
  variantItem,
  control,
  setValue,
  getValues,
  onMarkModified,
  currentUser,
}: any) => {
  const actualVariant = variantItem.variant || variantItem;
  const variantId = actualVariant?._id || "fallback";
  const fieldName = `variants.${variantId}.draftPO`;
  const initialDraftPO = variantItem?.priceCatalog?.draftPOEnabled ?? false;

  return (
    <Controller
      control={control}
      name={fieldName}
      defaultValue={initialDraftPO}
      render={({ field }) => (
        <Form.Check
          type="switch"
          checked={field.value ?? initialDraftPO} // Fallback injected here
          onChange={(e: { target: { checked: any; }; }) => {
            initVariantMetadata(getValues, setValue, product, variantItem);
            onMarkModified(product);
            field.onChange(e.target.checked);
          }}
          disabled={
            !Method.hasPermission(
              PermissionModule.PriceCatalog,
              Permissions.PC_EditDraftPOenabled,
              currentUser,
            )
          }
        />
      )}
    />
  );
};

// DOIH Control for Variant Row
const VariantDOIHControl = ({
  product,
  variantItem,
  control,
  setValue,
  getValues,
  onMarkModified,
  currentUser,
}: {
  product: any;
  variantItem: VariantWithPriceCatalog;
  control: any;
  setValue: any;
  getValues: any;
  onMarkModified: any;
  currentUser: UserModel | undefined;
}) => {
  const actualVariant = variantItem.variant || variantItem;
  const variantId = actualVariant?._id || "fallback";
  const fieldName = `variants.${variantId}.doih`;

  const initialDoih = variantItem?.priceCatalog?.daysOfInventoryHolding ?? 0;
  return (
    <Controller
      control={control}
      name={fieldName}
      defaultValue={initialDoih}
      render={({ field, fieldState }) => (
        <Form.Control
          type="number"
          size="sm"
          className="w-75px"
          isInvalid={!!fieldState.error}
          value={field.value !== undefined ? field.value : initialDoih}
          onChange={(e: { target: { value: any; }; }) => {
            initVariantMetadata(getValues, setValue, product, variantItem);
            onMarkModified(product);
            field.onChange(e.target.value);
          }}
          onBlur={(e: { target: { value: string; }; }) => {
            if (e.target.value === "" || Number.isNaN(Number(e.target.value))) {
              field.onChange(0);
            }
            field.onBlur();
          }}
          disabled={
            !Method.hasPermission(
              PermissionModule.PriceCatalog,
              Permissions.PC_EditDaysOfInventoryHolding,
              currentUser,
            )
          }
        />
      )}
    />
  );
};

// Isolated Row Component for Quantities
const QuantityRow = ({
  product,
  variantItem,
  qt,
  selectedWarehouse,
  control,
  setValue,
  getValues,
  onMarkModified,
  currentUser,
  healthyGP,
  minimumGP,
}: {
  product: PriceCatalogListingProduct;
  variantItem: VariantWithPriceCatalog;
  qt: QuantityType;
  selectedWarehouse: WarehouseItem;
  control: any;
  setValue: any;
  getValues: any;
  onMarkModified: any;
  currentUser: UserModel | undefined;
  healthyGP?: number;
  minimumGP?: number;
}) => {
  const actualVariant = variantItem.variant;
  const variantId = actualVariant?._id;
  const unitLabel =
    getQuantityTypeLabel(qt.type) +
    (qt.type !== 3
      ? `(${(actualVariant as any)?.[
      DbQuantityKeys[qt.type as keyof typeof DbQuantityKeys]
      ] ?? ""
      })`
      : "");

  const currentPrices = useMemo(
    () => getActivePrice(variantItem, qt.type, selectedWarehouse),
    [variantItem, qt.type, selectedWarehouse],
  );

  const cpField = `variants.${variantId}.quantities.${qt.type}.newCp`;
  const spField = `variants.${variantId}.quantities.${qt.type}.newSp`;

  const newCp = useWatch({
    control,
    name: cpField,
    defaultValue: currentPrices.cp,
  });
  const newSp = useWatch({
    control,
    name: spField,
    defaultValue: currentPrices.sp,
  });

  const margin = calculateGPMargin(Number(newCp || 0), Number(newSp || 0));
  const gpColorClass = getGPColorClass(
    Number(newCp || 0),
    Number(newSp || 0),
    healthyGP,
    minimumGP,
  );

  return (
    <tr>
      <td className="ps-12 text-muted fs-14 fw-bolder">{unitLabel}</td>
      <td>
        <Controller
          control={control}
          name={cpField}
          defaultValue={currentPrices.cp}
          render={({ field, fieldState }) => (
            <>
              <Form.Control
                type="number"
                size="sm"
                isInvalid={!!fieldState.error}
                name={field.name}
                value={
                  field.value !== undefined ? field.value : currentPrices.cp
                } // Fallback injected here
                onBlur={field.onBlur}
                onChange={(e: { target: { value: any; }; }) => {
                  initQuantityMetadata(
                    getValues,
                    setValue,
                    product,
                    variantItem,
                    qt,
                    currentPrices,
                    unitLabel,
                  );
                  onMarkModified(product);
                  field.onChange(e.target.value);
                }}
                onWheel={(e: { currentTarget: { blur: () => any; }; }) => e.currentTarget.blur()}
                placeholder="0"
                className="w-125px"
                disabled={
                  !Method.hasPermission(
                    PermissionModule.PriceCatalog,
                    Permissions.PC_EditCostPrice,
                    currentUser,
                  )
                }
              />
              {fieldState.error && (
                <div className="text-danger mt-1 fs-12">
                  {fieldState.error.message}
                </div>
              )}
            </>
          )}
        />
      </td>
      <td>
        <Controller
          control={control}
          name={spField}
          defaultValue={currentPrices.sp}
          render={({ field, fieldState }) => (
            <>
              <Form.Control
                type="number"
                size="sm"
                isInvalid={!!fieldState.error}
                name={field.name}
                value={
                  field.value !== undefined ? field.value : currentPrices.sp
                } // Fallback injected here
                onBlur={field.onBlur}
                onChange={(e: { target: { value: any; }; }) => {
                  initQuantityMetadata(
                    getValues,
                    setValue,
                    product,
                    variantItem,
                    qt,
                    currentPrices,
                    unitLabel,
                  );
                  onMarkModified(product);
                  field.onChange(e.target.value);
                }}
                onWheel={(e: { currentTarget: { blur: () => any; }; }) => e.currentTarget.blur()}
                placeholder="0"
                className="w-125px"
                disabled={
                  !Method.hasPermission(
                    PermissionModule.PriceCatalog,
                    Permissions.PC_EditSellingPrice,
                    currentUser,
                  )
                }
              />
              {fieldState.error && (
                <div className="text-danger mt-1 fs-12">
                  {fieldState.error.message}
                </div>
              )}
            </>
          )}
        />
      </td>
      <td>
        <span className={`fw-600 ${gpColorClass}`}>
          {margin}
        </span>
      </td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      <td>
        {Number(newCp || 0) === 0 && (
          <Badge
            bg="light"
            text="primary"
            className="border border-primary border-opacity-25"
          >
            No Cost Price
          </Badge>
        )}
      </td>
      <td></td>
    </tr>
  );
};

const PriceCatalogPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { selectedWarehouse, warehouseList } = useWarehouse();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState<number>(
    () => (getKey(listAssignProductsToVendors.page) as number) || 1,
  );
  const [pageLimit] = useState<number>(
    () => (getKey(listAssignProductsToVendors.limit) as number) || 30,
  );
  const [totalRecords, setTotalRecords] = useState(-1);
  const [products, setProducts] = useState<any[]>([]);
  const [search, setSearch] = useState<string>(
    (getKey(listAssignProductsToVendors.search) as string) || "",
  );
  const [enableForAllWarehouses, setEnableForAllWarehouses] =
    useState<boolean>(false);

  // Filter States
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [primaryCategories, setPrimaryCategories] = useState<CategoryType[]>(
    [],
  );
  const [subCategories, setSubCategories] = useState<CategoryType[]>([]);
  const [suppliersList, setSuppliersList] = useState<any[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandsList, setBrandsList] = useState<any[]>([]);
  const [selectedPrimary, setSelectedPrimary] = useState<CategoryType | null>(
    null,
  );
  const [selectedSub, setSelectedSub] = useState<CategoryType | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);

  // UI States
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    new Set(),
  );
  const [currentSection, setCurrentSection] = useState({
    title: "Price catalog",
    enable: true,
  });
  const [criticalAlertFilter, setCriticalAlertFilter] = useState<{
    value: string;
    label: string;
  } | null>(null);

  // Save confirmation States
  const [changesToConfirm, setChangesToConfirm] = useState<any[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Warehouse-wide GP roll-up shown on the "GP %" column header
  const [overallGrossProfit, setOverallGrossProfit] =
    useState<OverallGrossProfit | null>(null);

  // Warehouse-scoped Healthy/Minimum GP thresholds used to color-code each row's GP%
  const [gpThresholds, setGpThresholds] = useState<{
    healthy?: number;
    minimum?: number;
  }>({});

  // Cache to hold completely modified product objects so they aren't lost on Search/Pagination
  const editedProductsCache = useRef<Record<string, any>>({});

  const methods = useForm<BatchEditFormValues>({
    resolver: zodResolver(batchEditSchema as any),
    defaultValues: { variants: {}, products: {} },
    mode: "onChange",
  });

  const { control, setValue, getValues, reset } = methods;

  // Fetch categories with hierarchical depth
  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const params = {
        sortKey: "title",
        sortOrder: 1,
        categoriesDepth: 2,
      };
      const apiService = new APICallService(
        master.categoryList,
        params,
        "",
        "",
        "",
        "",
        Categories,
      );
      const response = await apiService.callAPI();
      if (response?.records) {
        setPrimaryCategories(response.records);

        // Restore previously selected category if available
        const savedPrimaryId = getKey(
          listAssignProductsToVendors.filterPrimary,
        )?.toString();
        const savedSubId = getKey(
          listAssignProductsToVendors.filterSub,
        )?.toString();

        const firstPrimary: any = savedPrimaryId
          ? response.records.find((c: any) => c._id === savedPrimaryId) ||
          response.records[0]
          : response.records[0];

        const subs = firstPrimary?.categories || [];
        setSubCategories(subs);

        const matchedSub = savedSubId
          ? subs.find((s: any) => s._id === savedSubId)
          : null;
        setSelectedSub(matchedSub);
      }
    } catch (err) {
      error("Failed to fetch categories");
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Fetch suppliers/vendors
  const fetchSuppliers = async () => {
    setSuppliersLoading(true);
    try {
      const params = {
        sortKey: "name",
        sortOrder: 1,
        needCount: true,
        "status[0]": 4, // Approved vendors
      };
      const apiService = new APICallService(
        vendor.listVendor,
        params,
        "",
        "",
        "",
        "",
      );
      const response = await apiService.callAPI();

      if (response?.records) {
        setSuppliersList(response.records);

        // Restore previously selected supplier if available
        const savedSupplierId = getKey(
          listAssignProductsToVendors.filterSupplier,
        )?.toString();
        if (savedSupplierId) {
          const supplier = response.records.find(
            (v: any) => v._id === savedSupplierId,
          );
          if (supplier) {
            setSelectedSupplier({
              ...supplier,
              value: supplier._id,
              label: supplier.ownerName,
            });
          }
        }
      }
    } catch (err) {
      error("Failed to fetch suppliers");
    } finally {
      setSuppliersLoading(false);
    }
  };

  // Fetch brands
  const fetchBrands = async () => {
    setBrandsLoading(true);
    try {
      const params = {
        sortKey: "title",
        sortOrder: 1,
        needCount: true,
      };
      const apiService = new APICallService(
        master.listBrands,
        params,
        "",
        "",
        "",
        "",
      );
      const response = await apiService.callAPI();

      if (response?.records) {
        setBrandsList(response.records);

        // Restore previously selected brand if available
        const savedBrandId = getKey(
          listAssignProductsToVendors.filterBrand,
        )?.toString();
        if (savedBrandId) {
          const brand = response.records.find(
            (b: any) => b._id === savedBrandId,
          );
          if (brand) {
            setSelectedBrand({
              ...brand,
              value: brand._id,
              label: brand.title,
            });
          }
        }
      }
    } catch (err) {
      error("Failed to fetch brands");
    } finally {
      setBrandsLoading(false);
    }
  };

  // Added a check for permission to access the page
  useEffect(() => {
    if (
      !(
        Method.hasModulePermission(
          PermissionModule.PriceCatalog,
          currentUser,
        ) &&
        Method.hasPermission(
          PermissionModule.PriceCatalog,
          Permissions.View,
          currentUser,
        )
      )
    ) {
      navigate("/dashboard");
    }
  }, []);

  // Initial fetch of categories, suppliers and brands
  useEffect(() => {
    fetchCategories();
    fetchSuppliers();
    fetchBrands();
  }, []);

  // Refetch products when warehouse changes
  useEffect(() => {
    if (selectedWarehouse?._id) {
      setPage(1);
      fetchOverallGrossProfit();
      fetchGPThresholds();
      fetchProducts(
        1,
        pageLimit,
        search,
        selectedPrimary?._id,
        selectedSub?._id,
        selectedSupplier?.value,
        false,
        selectedBrand?.value,
      );
    }
  }, [selectedWarehouse?._id]);

  const fetchOverallGrossProfit = async () => {
    try {
      const apiService = new APICallService(
        manageProduct.getPriceCatalogGrossProfit,
        { warehouse: selectedWarehouse?._id },
        "",
        "",
        "",
        "",
      );
      const response: Response<OverallGrossProfit> =
        await apiService.callAPI();
      setOverallGrossProfit(response?.data ?? null);
    } catch (err) {
      console.error("Failed to fetch overall gross profit", err);
      setOverallGrossProfit(null);
    }
  };

  // Fetches the warehouse-scoped Healthy/Minimum GP thresholds used to color-code the GP% column
  const fetchGPThresholds = async () => {
    try {
      const [healthyRecord, minimumRecord] = await Promise.all([
        fetchConfigurationSettingByKey(
          CONFIGURATION_SETTING_KEYS.KEY__HEALTHY_GROSS_PROFIT_THRESHOLD,
          selectedWarehouse?._id,
        ),
        fetchConfigurationSettingByKey(
          CONFIGURATION_SETTING_KEYS.KEY__MINIMUM_GROSS_PROFIT_THRESHOLD,
          selectedWarehouse?._id,
        ),
      ]);

      const healthy = Number(healthyRecord?.value);
      const minimum = Number(minimumRecord?.value);
      setGpThresholds({
        healthy: Number.isFinite(healthy) ? healthy : undefined,
        minimum: Number.isFinite(minimum) ? minimum : undefined,
      });
    } catch (err) {
      console.error("Failed to fetch GP thresholds", err);
      setGpThresholds({});
    }
  };

  const fetchProducts = async (
    pageNo: number,
    limit: number,
    searchTerm?: string,
    selectedPrimaryId?: string,
    selectedSubId?: string,
    selectedSupplierId?: string,
    append = false,
    selectedBrandId?: string,
  ) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    let params: any = {
      pageNo,
      limit,
      sortKey: "_createdAt",
      sortOrder: -1,
      state: AllProduct,
      status: ApprovedProduct,
      needCount: true,
      searchTerm: searchTerm || "",
      warehouse: selectedWarehouse?._id,
    };

    if (selectedPrimaryId) {
      params["category"] = selectedPrimaryId;
    }
    if (selectedSubId) {
      params["subCategory"] = selectedSubId;
    }
    if (selectedSupplierId) {
      params["vendor"] = selectedSupplierId;
    }
    if (selectedBrandId) {
      params["brand"] = selectedBrandId;
    }

    const apiCallService = new APICallService(
      manageProduct.getPriceCatalogProductList,
      params,
      "",
      "",
      "",
      "",
    );
    const response: ResponseWithRecord<ProductWithVariant[]> =
      await apiCallService.callAPI();
    if (response) {
      const fetchedRecords = response?.data.records || [];

      // 1. Identify which products currently have active, unsaved edits
      const currentVariants = getValues("variants") || {};
      const currentProducts = getValues("products") || {};
      const activeModifiedIds = new Set<string>();

      // Check variant level edits
      Object.values(currentVariants).forEach((v: any) => {
        if (!v?.productId) return;
        const draftChanged = v.draftPO !== v.oldDraftPO;
        const qtyChanged = hasQuantityChange(v.quantities);
        if (draftChanged || qtyChanged) activeModifiedIds.add(v.productId);
      });

      // Check product level edits
      Object.values(currentProducts).forEach((p: any) => {
        if (!p?.productId) return;
        if (
          p.isTaxable !== p.oldIsTaxable ||
          Number(p.taxPercentage) !== Number(p.oldTaxPercentage)
        ) {
          activeModifiedIds.add(p.productId);
        }
      });

      // 2. Prepend active modified products to the top, followed by unmodified fetched records
      const topProducts: any[] = [];
      const normalProducts: any[] = [];

      activeModifiedIds.forEach((id) => {
        if (editedProductsCache.current[id]) {
          const freshData = fetchedRecords.find((r) => r._id === id);
          if (freshData) editedProductsCache.current[id] = freshData;
          topProducts.push(editedProductsCache.current[id]);
          setExpandedProducts((prev) => new Set(prev).add(id));
        }
      });

      fetchedRecords.forEach((p) => {
        if (!activeModifiedIds.has(p._id)) {
          normalProducts.push(p);
        }
      });

      if (append) {
        setProducts((prev: any[]) => {
          const existingIds = new Set(prev.map((p: { _id: any; }) => p._id));
          const newRecords = [...topProducts, ...normalProducts].filter(
            (p) => !existingIds.has(p._id),
          );
          return [...prev, ...newRecords];
        });
      } else {
        setProducts([...topProducts, ...normalProducts]);
      }
      setTotalRecords(response?.data.total || 0);
    }
    if (append) setLoadingMore(false);
    else setLoading(false);
  };

  const loadMoreProducts = () => {
    if (loading || loadingMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProducts(
      nextPage,
      pageLimit,
      search,
      selectedPrimary?._id,
      selectedSub?._id,
      selectedSupplier?.value,
      true,
      selectedBrand?.value,
    );
  };

  const hasMore = !loading && products.length < totalRecords;

  const toggleProductExpand = (productId: string) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const onSubmitForm = (data: BatchEditFormValues) => {
    const variantPayloads = new Map<string, any>();

    // 1. Process explicit variant modifications (Draft PO, CP/SP)
    Object.values(data.variants || {}).forEach((v: any) => {
      if (!v?.productId) return;
      const draftChanged = v.draftPO !== v.oldDraftPO;
      const doihChanged = hasNumericChange(v.oldDoih, v.doih);
      const qtyChanged = hasQuantityChange(v.quantities);

      if (draftChanged || doihChanged || qtyChanged) {
        const firstVariant =
          v.originalVariantItem?.product?.variants?.[0] ||
          v.originalVariantItem;
        const fallbackTaxable =
          firstVariant?.priceCatalog?.taxed ?? !firstVariant?.product?.taxFree;
        const fallbackPct =
          firstVariant?.priceCatalog?.taxPercentage ??
          (firstVariant?.product?.productTax || 0);

        variantPayloads.set(v.variantId, {
          ...v,
          isTaxable: fallbackTaxable,
          taxPercentage: fallbackPct,
          oldIsTaxable: fallbackTaxable,
          oldTaxPercentage: fallbackPct,
          isProductLevelTaxChange: false,
        });
      }
    });

    // 2. Process product modifications (Tax, Tax %) applying to all underlying variants
    Object.values(data.products || {}).forEach((p: any) => {
      if (!p?.productId) return;
      const taxChanged = p.isTaxable !== p.oldIsTaxable;
      const taxPctChanged =
        Number(p.taxPercentage) !== Number(p.oldTaxPercentage);

      if (taxChanged || taxPctChanged) {
        const productItem = p.originalProductItem;
        productItem.variants?.forEach((variantItem: any) => {
          const actualVariant = variantItem.variant || variantItem;
          const vId = actualVariant?._id;

          if (variantPayloads.has(vId)) {
            // Merge tax data into existing variant payload
            const existing = variantPayloads.get(vId);
            existing.isTaxable = p.isTaxable;
            existing.taxPercentage = p.taxPercentage;
            existing.oldIsTaxable = p.oldIsTaxable;
            existing.oldTaxPercentage = p.oldTaxPercentage;
            existing.isProductLevelTaxChange = true;
          } else {
            // Create new variant payload explicitly for the tax change
            const initialDoih =
              variantItem?.priceCatalog?.daysOfInventoryHolding ?? 0;
            variantPayloads.set(vId, {
              productId: p.productId,
              variantId: vId,
              productTitle: p.productTitle,
              variantTitle: actualVariant?.title || "",
              originalVariantItem: variantItem,
              quantities: {},
              draftPO: variantItem?.priceCatalog?.draftPOEnabled ?? false,
              oldDraftPO: variantItem?.priceCatalog?.draftPOEnabled ?? false,
              doih: initialDoih,
              oldDoih: initialDoih,
              isTaxable: p.isTaxable,
              oldIsTaxable: p.oldIsTaxable,
              taxPercentage: p.taxPercentage,
              oldTaxPercentage: p.oldTaxPercentage,
              isProductLevelTaxChange: true,
            });
          }
        });
      }
    });

    if (variantPayloads.size === 0) {
      error("No price changes to save.");
      return;
    }

    setChangesToConfirm(Array.from(variantPayloads.values()));
    setShowConfirmModal(true);
  };

  const handleConfirmAndSave = async () => {
    setIsSaving(true);
    try {
      const payloads = changesToConfirm.map((variantData: any) => {
        const variantItem = variantData.originalVariantItem;
        const actualVariant = variantItem?.variant || variantItem;

        const mergedMap = new Map<number, any>();

        // 1 & 2. Seed the map using the same active-pricing lookup the UI uses
        // (getActivePrice filters by `active`, unlike a plain `.find` on pricing,
        // so this avoids picking up a stale/inactive pricing entry and zeroing costPrice).
        const existingPricing = variantItem?.priceCatalog?.pricing?.find(
          (p: any) => p.active && p.warehouseId === selectedWarehouse?._id,
        );
        const allTypes = new Set<number>(
          [
            ...(actualVariant?.quantityTypes || []),
            ...(existingPricing?.quantityTypes || []),
          ]
            .filter((qt: any) => isValidQuantityType(qt?.type))
            .map((qt: any) => Number(qt.type)),
        );
        allTypes.forEach((type) => {
          const { cp, sp } = getActivePrice(
            variantItem,
            type,
            selectedWarehouse!,
          );
          mergedMap.set(type, { type, costPrice: cp, sellingPrice: sp });
        });

        // 3. Overlay any explicit quantity changes made via the form
        Object.values(variantData.quantities || {}).forEach((c: any) => {
          if (!c || !isValidQuantityType(c.type)) return;
          if (
            hasNumericChange(c.oldCp, c.newCp) ||
            hasNumericChange(c.oldSp, c.newSp)
          ) {
            mergedMap.set(Number(c.type), {
              type: Number(c.type),
              costPrice: Number(c.newCp) || 0,
              sellingPrice: Number(c.newSp) || 0,
            });
          }
        });

        const pricingEntry = {
          active: true,
          warehouseId: selectedWarehouse?._id,
          defaultWarehouse: selectedWarehouse?.defaultWarehouse ?? false,
          quantityTypes: Array.from(mergedMap.values()),
        };

        const pricingEntryArr = [];

        if (enableForAllWarehouses) {
          for (let i in warehouseList) {
            pricingEntryArr.push({
              ...pricingEntry,
              warehouseId: warehouseList[i]._id,
              defaultWarehouse: warehouseList[i].defaultWarehouse,
            });
          }
        }

        return {
          productId: variantData.productId,
          variantId: variantData.variantId,
          title: variantData.variantTitle,
          taxed: variantData.isTaxable,
          taxPercentage: Number(variantData.taxPercentage)
            ? Number(variantData.taxPercentage)
            : 18,
          enableForAllWarehouses,
          draftPOEnabled: variantData.draftPO,
          daysOfInventoryHolding: Number(variantData.doih) || 0,
          pricing:
            pricingEntryArr.length > 0 ? pricingEntryArr : [pricingEntry],
        };
      });

      const responses: Response<PriceCatalog> = await new APICallService(
        manageProduct.savePriceCatalogEntry,
        payloads,
        "",
        "",
        "",
        "",
      ).callAPI();

      if (!responses.error) {
        success("Prices updated successfully!");
        reset({ variants: {}, products: {} });
        editedProductsCache.current = {};
        setShowConfirmModal(false);
        fetchOverallGrossProfit();
        // Re-fetch every page loaded so far (not just `page`) to keep the same list/order intact
        fetchProducts(
          1,
          page * pageLimit,
          search,
          selectedPrimary?._id,
          selectedSub?._id,
          selectedSupplier?.value,
          false,
          selectedBrand?.value,
        );
      } else {
        error(responses.message);
      }
    } catch (err) {
      error("Error saving catalog.");
    } finally {
      setIsSaving(false);
      setEnableForAllWarehouses(false);
    }
  };

  const debounce = useDebounce(fetchProducts, 1000);

  useEffect(() => {
    setPage(1);
    debounce(
      1,
      pageLimit,
      search,
      selectedPrimary?._id,
      selectedSub?._id,
      selectedSupplier?.value,
      false,
      selectedBrand?.value,
    );
  }, [search, selectedPrimary, selectedSub, selectedSupplier, selectedBrand]);

  // Handlers for filter changes
  const handlePrimaryChange = async (option: any) => {
    const selected = primaryCategories.find(
      (item: { _id: any; }) => item._id === option?.value,
    );
    setSelectedPrimary(selected || null);
    setSubCategories(selected?.categories || []);
    setSelectedSub(null);
    setPage(1);
    setKey(listAssignProductsToVendors.filterPrimary, option?.value || "");
    await fetchProducts(
      1,
      pageLimit,
      search,
      selected?._id,
      "",
      selectedSupplier?.value,
      false,
      selectedBrand?.value,
    );
  };

  const handleSubChange = async (option: any) => {
    const selected = subCategories.find(
      (item: { _id: any; }) => item._id === (option?.value || ""),
    );
    setSelectedSub(selected || null);
    setPage(1);
    setKey(listAssignProductsToVendors.filterSub, option?.value || "");
    await fetchProducts(
      1,
      pageLimit,
      search,
      selectedPrimary?._id,
      selected?._id,
      selectedSupplier?.value,
      false,
      selectedBrand?.value,
    );
  };

  const handleSupplierChange = async (option: any) => {
    setSelectedSupplier(option || null);
    setPage(1);
    setKey(listAssignProductsToVendors.filterSupplier, option?.value || "");
    await fetchProducts(
      1,
      pageLimit,
      search,
      selectedPrimary?._id,
      selectedSub?._id,
      option?.value,
      false,
      selectedBrand?.value,
    );
  };

  const handleBrandChange = async (option: any) => {
    setSelectedBrand(option || null);
    setPage(1);
    setKey(listAssignProductsToVendors.filterBrand, option?.value || "");
    await fetchProducts(
      1,
      pageLimit,
      search,
      selectedPrimary?._id,
      selectedSub?._id,
      selectedSupplier?.value,
      false,
      option?.value,
    );
  };

  const markProductModified = (p: any) => {
    if (!editedProductsCache.current[p._id]) {
      editedProductsCache.current[p._id] = p;
    }
  };

  const goToCriticalAlertsForVariant = (variantId: string, label: string) => {
    if (
      !Method.hasPermission(
        PermissionModule.PriceCatalog,
        Permissions.PC_CriticalAlerts,
        currentUser,
      )
    ) {
      return;
    }
    setCriticalAlertFilter({ value: variantId, label });
    setCurrentSection({ title: "Critical Alert", enable: true });
  };

  return (
    <div className="p-5 bg-f9f9f9 min-vh-100">
      <div className="d-flex justify-content-center mb-6">
        <RoundedSwitch
          activeTitle={currentSection.title}
          onToggle={(item: any) => {
            setCurrentSection(item);
            setCriticalAlertFilter(null);
          }}
          items={[
            { title: "Price catalog", enable: true },
            {
              title: "Critical Alert",
              enable: false,
              disabled: !Method.hasPermission(
                PermissionModule.PriceCatalog,
                Permissions.PC_CriticalAlerts,
                currentUser,
              ),
            },
            {
              title: "Change History",
              enable: false,
              disabled: !Method.hasPermission(
                PermissionModule.PriceCatalog,
                Permissions.PC_ChangeHistory,
                currentUser,
              ),
            },
          ]}
        />
      </div>
      {currentSection.title === "Price catalog" && currentSection.enable ? (
        <>
          {/* Form wrapping has submit intercept removed to prevent accidental enter-key blocks */}
          <Form onSubmit={(e: { preventDefault: () => any; }) => e.preventDefault()}>
            {/* Header Area */}
            <div className="d-flex align-items-center justify-content-between mb-5">
              <div className="d-flex align-items-center gap-2">
                <h1 className="fs-22 fw-bolder mb-0">Price Catalog</h1>
              </div>
              <div className="d-flex align-items-center gap-4">
                <SaveAllButton
                  control={control}
                  onSave={() => onSubmitForm(getValues())}
                />
                {
                  selectedWarehouse?.defaultWarehouse &&
                  <Form.Check
                    label="All warehouses"
                    type="switch"
                    checked={enableForAllWarehouses}
                    onChange={(e: { target: { checked: any; }; }) => setEnableForAllWarehouses(e.target.checked)}
                    disabled={
                      !Method.hasPermission(
                        PermissionModule.PriceCatalog,
                        Permissions.PC_All_Warehouses,
                        currentUser,
                      )
                    }
                  />
                }
              </div>
            </div>

            {/* Filters Section */}
            <Card className="border-0 shadow-sm mb-5">
              <Card.Body className="px-7 py-6">
                <Row className="align-items-end gap-5 mb-3">
                  <Col xs={12}>
                    <label className="form-label fw-bold mb-2">Product</label>
                    <CustomSearchInput
                      placeholder="Search products..."
                      defaultValue={search}
                      onSearch={(v: any) => {
                        setSearch(v);
                      }}
                    />
                  </Col>
                </Row>
                <Row className="align-items-end g-5">
                  <Col xs={12} sm={6} md={3}>
                    <div className="w-100">
                      <label className="form-label fw-bold mb-2">
                        Category
                      </label>
                      <CustomSelect
                        backgroundColor="#ffffff"
                        placeholder="Select category"
                        isSearchable={true}
                        isClearable={true}
                        isLoading={categoriesLoading}
                        options={primaryCategories.map((cat: { _id: any; title: any; }) => ({
                          value: cat._id,
                          label: cat.title,
                        }))}
                        value={
                          selectedPrimary
                            ? {
                              value: selectedPrimary._id,
                              label: selectedPrimary.title,
                            }
                            : null
                        }
                        onChange={handlePrimaryChange}
                      />
                    </div>
                  </Col>

                  <Col xs={12} sm={6} md={3}>
                    <div className="w-100">
                      <label className="form-label fw-bold mb-2">
                        Subcategory
                      </label>
                      <CustomSelect
                        backgroundColor="#ffffff"
                        placeholder="Select subcategory"
                        isSearchable={true}
                        isClearable={true}
                        isDisabled={!selectedPrimary}
                        options={subCategories.map((subcat: { _id: any; title: any; }) => ({
                          value: subcat._id,
                          label: subcat.title,
                        }))}
                        value={
                          selectedSub
                            ? {
                              value: selectedSub._id,
                              label: selectedSub.title,
                            }
                            : null
                        }
                        onChange={handleSubChange}
                      />
                    </div>
                  </Col>

                  <Col xs={12} sm={6} md={3}>
                    <div className="w-100">
                      <label className="form-label fw-bold mb-2">Vendor</label>
                      <CustomSelect
                        backgroundColor="#ffffff"
                        placeholder="Select supplier"
                        isSearchable={true}
                        isClearable={true}
                        isLoading={suppliersLoading}
                        options={suppliersList.map((vendor: { _id: any; ownerName: any; }) => ({
                          value: vendor._id,
                          label: vendor.ownerName || "Unknown",
                        }))}
                        value={selectedSupplier}
                        onChange={handleSupplierChange}
                      />
                    </div>
                  </Col>

                  <Col xs={12} sm={6} md={3}>
                    <div className="w-100">
                      <label className="form-label fw-bold mb-2">Brand</label>
                      <CustomSelect
                        backgroundColor="#ffffff"
                        placeholder="Select brand"
                        isSearchable={true}
                        isClearable={true}
                        isLoading={brandsLoading}
                        options={brandsList.map((brand: { _id: any; title: any; }) => ({
                          value: brand._id,
                          label: brand.title,
                        }))}
                        value={selectedBrand}
                        onChange={handleBrandChange}
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <InfiniteScroll
              hasMore={hasMore}
              loading={loadingMore}
              onLoadMore={loadMoreProducts}
            >
              <Card className="border-0 shadow-sm">
                <div className="table-responsive">
                  <table className="table table-row-bordered align-middle gs-4 gy-4 mb-0">
                    <thead className="bg-light text-muted fw-bold fs-14 text-uppercase">
                      <tr>
                        <th className="min-w-250px ps-4">Product / Variant</th>
                        <th className="min-w-150px">Cost Price</th>
                        <th className="min-w-150px">Selling Price</th>
                        <th
                          className="min-w-100px"
                        >
                          <OverlayTrigger
                            placement="top"
                            // rendered on body so the thead's text-muted/uppercase rules don't bleed into the tooltip
                            container={document.body}
                            overlay={
                              <Tooltip id="price-catalog-gp-tooltip">
                                <div
                                  className="text-start"
                                  style={{
                                    backgroundColor: getGPBgColor(
                                      overallGrossProfit?.currentGP,
                                      gpThresholds.healthy,
                                      gpThresholds.minimum,
                                    ),
                                    color: "#fff",
                                    // negative margin cancels .tooltip-inner's own padding so this
                                    // fill covers the whole tooltip box instead of leaving its default
                                    // dark background showing as a border around the content
                                    margin: "-0.75rem -1rem",
                                    padding: "0.75rem 1rem",
                                    borderRadius: "0.475rem",
                                  }}
                                >
                                  <div>
                                    Current GP:{" "}
                                    {formatGP(overallGrossProfit?.currentGP)}
                                  </div>
                                  <div>
                                    Average GP:{" "}
                                    {formatGP(overallGrossProfit?.averageGP)}
                                  </div>
                                </div>
                              </Tooltip>
                            }
                          >
                            <span style={{ cursor: "help" }}>GP %</span>
                          </OverlayTrigger>
                        </th>
                        <th className="min-w-90px">Draft PO</th>
                        <th className="min-w-80px">DOIH</th>
                        <th className="min-w-80px">Taxable</th>
                        <th className="min-w-80px">Tax %</th>
                        <th className="min-w-120px">Alert</th>
                        <th className="min-w-80px text-end pe-4">Variants</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={11} className="text-center py-5">
                            <Loader loading={true} />
                          </td>
                        </tr>
                      ) : products.length === 0 ? (
                        <tr>
                          <td
                            colSpan={11}
                            className="text-center py-5 text-muted"
                          >
                            No products found.
                          </td>
                        </tr>
                      ) : (
                        products.map((product: PriceCatalogListingProduct) => {
                          const isExpanded = expandedProducts.has(product._id);
                          const variantCount = product.variants?.length || 0;

                          return (
                            <React.Fragment key={product._id}>
                              {/* Top Level: Product Row */}
                              <tr className="bg-light bg-opacity-50">
                                <td className="ps-4">
                                  <div className="d-flex align-items-center gap-3">
                                    <i
                                      className={`bi bi-${isExpanded ? "dash" : "plus"
                                        }-square text-muted fs-4`}
                                      style={{ cursor: "pointer" }}
                                      onClick={() =>
                                        toggleProductExpand(product._id)
                                      }
                                    ></i>
                                    <span className="fw-bolder text-dark fs-15">
                                      {product.title || "-"}
                                    </span>
                                  </div>
                                </td>
                                <td className="text-muted"></td>
                                <td className="text-muted"></td>
                                <td className="text-muted"></td>
                                <td className="text-muted"></td>
                                <td className="text-muted"></td>

                                {/* Custom Tax Controls */}
                                <ProductTaxControls
                                  product={product}
                                  control={control}
                                  setValue={setValue}
                                  getValues={getValues}
                                  onMarkModified={markProductModified}
                                  currentUser={currentUser}
                                />

                                <td></td>
                                <td className="text-end pe-4">
                                  <span
                                    className="fw-bold text-dark cursor-pointer text-decoration-underline"
                                    onClick={() =>
                                      toggleProductExpand(product._id)
                                    }
                                  >
                                    {isExpanded
                                      ? `Hide ${variantCount} variants`
                                      : `View ${variantCount} variant${variantCount > 1 ? "s" : ""
                                      }`}
                                  </span>
                                </td>
                              </tr>

                              {/* Expanded Level: Variants & Quantity Types */}
                              {isExpanded &&
                                product.variants?.map(
                                  (
                                    variantItem: VariantWithPriceCatalog,
                                    vIdx: number,
                                  ) => {
                                    const actualVariant = variantItem.variant;
                                    const variantId =
                                      actualVariant?._id || `fallback-${vIdx}`;
                                    const variantTitle =
                                      actualVariant?.title || "";

                                    // Filter to unique quantity types based on `type` alone
                                    const uniqueQuantityTypes = Array.from(
                                      new Map(
                                        (actualVariant?.quantityTypes || [])
                                          .filter((qt: any) =>
                                            isValidQuantityType(qt?.type),
                                          )
                                          .map((qt: any) => [
                                            Number(qt.type),
                                            qt,
                                          ]),
                                      ).values(),
                                    ) as any[];

                                    return (
                                      <React.Fragment key={variantId}>
                                        {/* Variant Row */}
                                        <tr>
                                          <td
                                            className="ps-9 fw-600 fs-14 text-dark"
                                            style={{
                                              maxWidth: 250,
                                              whiteSpace: "normal",
                                              wordBreak: "break-word",
                                              overflowWrap: "anywhere",
                                            }}
                                          >
                                            {variantTitle ? variantTitle : "-"}
                                          </td>
                                          <td className="text-muted fs-14"></td>
                                          <td className="text-muted"></td>
                                          <td className="text-muted"></td>
                                          <td>
                                            <VariantDraftPOControl
                                              product={product}
                                              variantItem={variantItem}
                                              control={control}
                                              setValue={setValue}
                                              getValues={getValues}
                                              onMarkModified={
                                                markProductModified
                                              }
                                              currentUser={currentUser}
                                            />
                                          </td>
                                          <td>
                                            <VariantDOIHControl
                                              product={product}
                                              variantItem={variantItem}
                                              control={control}
                                              setValue={setValue}
                                              getValues={getValues}
                                              onMarkModified={
                                                markProductModified
                                              }
                                              currentUser={currentUser}
                                            />
                                          </td>
                                          <td className="text-muted"></td>
                                          <td className="text-muted"></td>
                                          <td
                                            className={`m-3 ${variantItem?.priceCatalog?.criticalAlerts?.find(
                                              (key) => key.tier === 1,
                                            )
                                              ? "bg-danger"
                                              : "bg-warning"
                                              } text-white rounded-circle d-inline-flex align-items-center justify-content-center fw-bold`}
                                            style={{
                                              width: "30px",
                                              height: "30px",
                                              cursor: "pointer",
                                            }}
                                            title="View critical alerts for this variant"
                                            onClick={() =>
                                              goToCriticalAlertsForVariant(
                                                variantId,
                                                variantTitle || product.title,
                                              )
                                            }
                                          >
                                            {variantItem.priceCatalog.criticalAlerts?.filter(
                                              (key) =>
                                                key.warehouseId ===
                                                selectedWarehouse?._id,
                                            )?.length || 0}
                                          </td>
                                          <td></td>
                                        </tr>

                                        {/* Quantity Types Row Map */}
                                        {uniqueQuantityTypes.map((qt: any) => (
                                          <QuantityRow
                                            key={`${variantId}_${qt.type}`}
                                            product={product}
                                            variantItem={variantItem}
                                            qt={qt}
                                            selectedWarehouse={
                                              selectedWarehouse!
                                            }
                                            control={control}
                                            setValue={setValue}
                                            getValues={getValues}
                                            onMarkModified={markProductModified}
                                            currentUser={currentUser}
                                            healthyGP={gpThresholds.healthy}
                                            minimumGP={gpThresholds.minimum}
                                          />
                                        ))}
                                      </React.Fragment>
                                    );
                                  },
                                )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </InfiniteScroll>
          </Form>

          {showConfirmModal && (
            <ConfirmChangesModal
              show={showConfirmModal}
              onHide={() => setShowConfirmModal(false)}
              changes={changesToConfirm}
              onConfirm={handleConfirmAndSave}
              isSaving={isSaving}
              enableForAllWarehouses={enableForAllWarehouses}
            />
          )}
        </>
      ) : currentSection.title === "Critical Alert" && currentSection.enable ? (
        <CriticalAlertsTab initialProductFilter={criticalAlertFilter} />
      ) : (
        <ChangeHistoryTab warehouseId={selectedWarehouse?._id} />
      )}
    </div>
  );
};

export default PriceCatalogPage;
